// SCR-STAFF-406 — فروش و مدیریت پکیج (US-BIL-070، 072 تا 074؛ FLOW-11): انتخاب مراجعه‌کننده، فروش پکیج منتشرشده با پیش‌نمایش قیمت، تخفیف و ارزش
// تخصیص‌یافته هر جلسه (C-AGENT-813)، صدور و رفتن به صندوق؛ فهرست پکیج‌های مراجعه‌کننده با جلسات، انقضا و مانده؛ جزئیات اعتبارها، انتقال،
// تمدید با هزینه و لغو با محاسبه استرداد (C-AGENT-818، 827، 828). عضویت فاز 2 است (C-AGENT-821).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type BilPackagePolicyView,
  type BilPackageSaleView,
  type BilSellPackageResponse,
  type PatientSearchItem,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, faDigits, FormDialog, JalaliDate, KeyValueList, ListPage, LoadingState, ReasonDialog } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router';
import { PatientPicker } from '../patients/PatientPicker';
import { Amount, bilKeys, cashierPatientPath, CreditStatusChip, newQuotePath, num, PackageStatusChip, SCR_STAFF_406 } from './common';

/** کدهای کوپن ورودی (جداشده با فاصله یا ویرگول؛ حداکثر ۱۰ — C-AGENT-822). */
export function parseCodes(text: string): string[] {
  return text
    .split(/[\s,،]+/)
    .map((code) => code.trim())
    .filter((code) => code.length > 0)
    .slice(0, 10);
}

export function PackageSalesPage() {
  const { t } = useTranslation('bil');
  const [params, setParams] = useSearchParams();
  const access = useAccess();
  const patientId = params.get('patientId') ?? '';
  const [picked, setPicked] = useState<PatientSearchItem | null>(null);
  return (
    <ListPage
      screenId={SCR_STAFF_406}
      titleKey="bil:packages.title"
      subtitleKey="bil:packages.subtitle"
      actions={
        patientId && access.can('bil.quote.create') ? (
          <Button component={RouterLink} to={newQuotePath(patientId)} data-testid="bil-packages-new-quote">
            {t('bil:quotes.new')}
          </Button>
        ) : undefined
      }
      filters={
        <PatientPicker
          labelKey="bil:packages.patient"
          value={picked}
          onChange={(value) => {
            setPicked(value);
            setParams(value ? { patientId: value.id } : {});
          }}
          testId="bil-packages-patient"
        />
      }
    >
      {patientId ? (
        <PatientPackagesPanel patientId={patientId} />
      ) : (
        <Typography color="text.secondary" data-testid="bil-packages-pick">
          {t('bil:packages.pick_patient')}
        </Typography>
      )}
    </ListPage>
  );
}

/** پکیج‌های یک مراجعه‌کننده با اقدام‌ها؛ در SCR-STAFF-408 هم نمایش داده می‌شود. */
export function PatientPackagesPanel({ patientId }: { patientId: string }) {
  const { t } = useTranslation('bil');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const [selling, setSelling] = useState(false);
  const [sold, setSold] = useState<BilSellPackageResponse | null>(null);
  const [detail, setDetail] = useState<BilPackageSaleView | null>(null);
  const [transferring, setTransferring] = useState<BilPackageSaleView | null>(null);
  const [target, setTarget] = useState<PatientSearchItem | null>(null);
  const [extending, setExtending] = useState<BilPackageSaleView | null>(null);
  const [days, setDays] = useState('30');
  const [cancelling, setCancelling] = useState<BilPackageSaleView | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const sales = useQuery({
    queryKey: bilKeys.packageSales(session?.tenantId, patientId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/billing/package-sales', { params: { query: { patientId } } })),
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: bilKeys.all(session?.tenantId) });
  const transfer = useMutation({
    mutationFn: (sale: BilPackageSaleView) =>
      unwrap(
        tenantApi.POST('/api/v1/billing/package-sales/{id}:transfer', {
          params: { path: { id: sale.id } },
          body: { targetPatientId: target?.id ?? '', reason: null },
          headers: { 'Idempotency-Key': key.current() },
        }),
      ),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: () => {
      setTransferring(null);
      setTarget(null);
      setNotice(t('bil:packages.transferred'));
      refresh();
    },
  });
  const extend = useMutation({
    mutationFn: (sale: BilPackageSaleView) =>
      unwrap(
        tenantApi.POST('/api/v1/billing/package-sales/{id}:extend', {
          params: { path: { id: sale.id } },
          body: { days: Number(days) || 0, reason: null },
          headers: { 'Idempotency-Key': key.current() },
        }),
      ),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: (result) => {
      setExtending(null);
      setNotice(result.feeInvoiceNumber ? t('bil:packages.extended_fee', { number: result.feeInvoiceNumber }) : t('bil:packages.extended'));
      refresh();
    },
  });

  if (sales.isPending) {
    return <LoadingState />;
  }
  if (sales.error) {
    return <ErrorState error={sales.error} onRetry={() => void sales.refetch()} />;
  }
  const full = access.accessMode === 'Full';
  const items = sales.data.items;
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="bil-patient-packages">
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="subtitle1" component="h2" sx={{ flex: 1 }}>
          {t('bil:packages.list')}
        </Typography>
        {full && access.can('bil.package.sell') ? (
          <Button variant="contained" onClick={() => setSelling(true)} data-testid="bil-package-sell">
            + {t('bil:packages.sell')}
          </Button>
        ) : null}
      </Stack>
      {notice ? (
        <Alert severity="success" onClose={() => setNotice(null)} data-testid="bil-packages-notice" sx={{ mb: 1 }}>
          {notice}
        </Alert>
      ) : null}
      {sold ? (
        <Alert
          severity="success"
          data-testid="bil-package-sold"
          sx={{ mb: 1 }}
          onClose={() => setSold(null)}
          action={
            access.can('bil.payment.receive') ? (
              <Button component={RouterLink} to={cashierPatientPath(patientId)} size="small" data-testid="bil-package-to-cashier">
                {t('bil:packages.to_cashier')}
              </Button>
            ) : undefined
          }
        >
          {t('bil:packages.sold', { number: sold.invoiceNumber })}
        </Alert>
      ) : null}
      {items.length === 0 ? (
        <Typography color="text.secondary">{t('bil:packages.empty')}</Typography>
      ) : (
        <Table size="small" aria-label={t('bil:packages.list')}>
          <TableHead>
            <TableRow>
              <TableCell>{t('bil:packages.columns.name')}</TableCell>
              <TableCell>{t('bil:packages.columns.status')}</TableCell>
              <TableCell>{t('bil:packages.columns.sessions')}</TableCell>
              <TableCell>{t('bil:packages.columns.expires')}</TableCell>
              <TableCell>{t('bil:packages.columns.remaining')}</TableCell>
              <TableCell>{t('bil:packages.columns.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((sale) => (
              <TableRow key={sale.id} data-testid="bil-package-row" data-sale-id={sale.id}>
                <TableCell>{sale.packageName}</TableCell>
                <TableCell>
                  <PackageStatusChip status={sale.status} />
                </TableCell>
                <TableCell data-testid="bil-package-sessions">
                  {t('bil:packages.sessions_of', { available: faDigits(num(sale.availableSessions)), total: faDigits(num(sale.totalSessions)) })}
                </TableCell>
                <TableCell>
                  <JalaliDate value={sale.expiresOn} />
                </TableCell>
                <TableCell>
                  <Amount money={sale.remainingValue} />
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
                    <Button size="small" onClick={() => setDetail(sale)} data-testid="bil-package-detail">
                      {t('bil:packages.detail')}
                    </Button>
                    {full && sale.actions.canTransfer && access.can('bil.package.transfer') ? (
                      <Button size="small" onClick={() => setTransferring(sale)} data-testid="bil-package-transfer">
                        {t('bil:packages.transfer')}
                      </Button>
                    ) : null}
                    {full && sale.actions.canExtend && access.can('bil.package.extend') ? (
                      <Button
                        size="small"
                        onClick={() => {
                          setDays(String(Math.min(30, num(sale.maxExtensionDays))));
                          setExtending(sale);
                        }}
                        data-testid="bil-package-extend"
                      >
                        {t('bil:packages.extend')}
                      </Button>
                    ) : null}
                    {full && sale.actions.canCancel && access.can('bil.package.cancel') ? (
                      <Button size="small" color="error" onClick={() => setCancelling(sale)} data-testid="bil-package-cancel">
                        {t('bil:packages.cancel')}
                      </Button>
                    ) : null}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {selling ? (
        <SellPackageDialog
          patientId={patientId}
          onClose={() => setSelling(false)}
          onSold={(result) => {
            setSelling(false);
            setSold(result);
            refresh();
          }}
        />
      ) : null}
      {detail ? <PackageDetailDialog sale={detail} onClose={() => setDetail(null)} /> : null}
      <FormDialog
        open={transferring !== null}
        titleKey="bil:packages.transfer"
        onSubmit={() => transferring && target && transfer.mutate(transferring)}
        onClose={() => {
          setTransferring(null);
          transfer.reset();
        }}
        isDirty={target !== null}
        isSubmitting={transfer.isPending}
        submitError={transfer.error}
      >
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2">{t('bil:packages.transfer_hint', { sessions: faDigits(num(transferring?.availableSessions)) })}</Typography>
          <PatientPicker labelKey="bil:packages.transfer_to" value={target} onChange={setTarget} excludeIds={[patientId]} required testId="bil-package-transfer-target" />
        </Stack>
      </FormDialog>
      <FormDialog
        open={extending !== null}
        titleKey="bil:packages.extend"
        onSubmit={() => extending && extend.mutate(extending)}
        onClose={() => {
          setExtending(null);
          extend.reset();
        }}
        isDirty
        isSubmitting={extend.isPending}
        submitError={extend.error}
      >
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            type="number"
            label={t('bil:packages.extend_days')}
            value={days}
            onChange={(event) => setDays(event.target.value)}
            helperText={t('bil:packages.extend_max', { days: faDigits(num(extending?.maxExtensionDays)) })}
            slotProps={{ htmlInput: { min: 1, max: num(extending?.maxExtensionDays), 'data-testid': 'bil-package-extend-days' } }}
          />
          {extending && extending.policy.extensionFee.amount > 0 ? (
            <Typography variant="body2" data-testid="bil-package-extend-fee">
              {t('bil:packages.extend_fee')} <Amount money={extending.policy.extensionFee} />
            </Typography>
          ) : null}
        </Stack>
      </FormDialog>
      {cancelling ? (
        <CancelPackageDialog
          sale={cancelling}
          onClose={() => setCancelling(null)}
          onDone={(number) => {
            setCancelling(null);
            setNotice(number ? t('bil:packages.cancelled_note', { number }) : t('bil:packages.cancelled'));
            refresh();
          }}
        />
      ) : null}
    </Paper>
  );
}

function PolicySummary({ policy }: { policy: BilPackagePolicyView }) {
  const { t } = useTranslation('bil');
  return (
    <Typography variant="body2" color="text.secondary" data-testid="bil-package-policy">
      {[
        policy.transferable ? t('bil:packages.policy.transferable') : t('bil:packages.policy.not_transferable'),
        policy.cancellable
          ? t('bil:packages.policy.penalty', { percent: faDigits(num(policy.cancellationPenaltyBasisPoints) / 100) })
          : t('bil:packages.policy.not_cancellable'),
        num(policy.maxExtensions) > 0 ? t('bil:packages.policy.extensions', { count: num(policy.maxExtensions) }) : t('bil:packages.policy.no_extension'),
      ].join(' · ')}
    </Typography>
  );
}

/** فروش پکیج (FLOW-11): انتخاب، کوپن، پیش‌نمایش ارزش جلسات و صدور. */
function SellPackageDialog({ patientId, onClose, onSold }: { patientId: string; onClose: () => void; onSold: (result: BilSellPackageResponse) => void }) {
  const { t } = useTranslation('bil');
  const { tenantApi, session } = useTenantSession();
  const navigate = useNavigate();
  const access = useAccess();
  const key = useIdempotencyKey();
  const [packageId, setPackageId] = useState('');
  const [codesText, setCodesText] = useState('');
  const [toCashier, setToCashier] = useState(access.can('bil.payment.receive'));
  const codes = parseCodes(codesText);
  const branchId = session?.activeBranchId ?? null;
  const sellable = useQuery({
    queryKey: bilKeys.sellable(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/billing/packages', { params: { query: { branchId: branchId ?? undefined } } })),
  });
  const body = { patientId, packageId, branchId, codes, ruleIds: null };
  const preview = useQuery({
    queryKey: ['billing', session?.tenantId, 'package-preview', patientId, packageId, codes.join(',')],
    queryFn: () => unwrap(tenantApi.POST('/api/v1/billing/package-sales:preview', { body })),
    enabled: packageId !== '',
    retry: false,
  });
  const sell = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/billing/package-sales', { body, headers: { 'Idempotency-Key': key.current() } })),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: (result) => {
      if (toCashier) {
        void navigate(cashierPatientPath(patientId));
        return;
      }
      onSold(result);
    },
  });
  const options = sellable.data?.items ?? [];
  const chosen = options.find((item) => item.packageId === packageId) ?? null;
  return (
    <FormDialog
      open
      titleKey="bil:packages.sell"
      screenId={SCR_STAFF_406}
      onSubmit={() => packageId && preview.data && sell.mutate()}
      onClose={onClose}
      isDirty={packageId !== ''}
      isSubmitting={sell.isPending}
      submitError={sell.error}
      submitKey="bil:packages.issue"
      maxWidth="md"
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        {sellable.error ? <ErrorState error={sellable.error} compact /> : null}
        <TextField
          select
          required
          label={t('bil:packages.package')}
          value={packageId}
          onChange={(event) => setPackageId(event.target.value)}
          slotProps={{ htmlInput: { 'data-testid': 'bil-package-select' } }}
        >
          {options.map((item) => (
            <MenuItem key={item.packageId} value={item.packageId} disabled={!item.supported}>
              {item.name} · {faDigits(num(item.totalSessions))} {t('bil:packages.session_unit')}
              {item.supported ? '' : ` (${t('bil:packages.unsupported')})`}
            </MenuItem>
          ))}
        </TextField>
        {chosen ? (
          <Typography variant="body2" data-testid="bil-package-price">
            {t('bil:packages.price')} <Amount money={chosen.price} /> · {t('bil:packages.validity', { days: faDigits(num(chosen.validityDays)) })}
          </Typography>
        ) : null}
        <TextField
          label={t('bil:packages.codes')}
          value={codesText}
          onChange={(event) => setCodesText(event.target.value)}
          helperText={t('bil:packages.codes_hint')}
          slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'bil-package-codes' } }}
        />
        {preview.error ? <ErrorState error={preview.error} compact /> : null}
        {preview.data ? (
          <Paper variant="outlined" sx={{ p: 2 }} data-testid="bil-package-preview">
            <KeyValueList
              columns={3}
              items={[
                { labelKey: 'bil:packages.gross', value: <Amount money={preview.data.gross} testId="bil-package-gross" /> },
                { labelKey: 'bil:packages.discount', value: <Amount money={preview.data.discount} testId="bil-package-discount" /> },
                { labelKey: 'bil:packages.net', value: <Amount money={preview.data.net} testId="bil-package-net" /> },
              ]}
            />
            {preview.data.discounts.map((discount) => (
              <Typography key={discount.ruleId} variant="body2" color="warning.main" data-testid="bil-package-applied-discount">
                {t('bil:packages.applied_discount', { rule: discount.ruleCode ?? discount.couponCode ?? '' })} <Amount money={discount.amount} />
              </Typography>
            ))}
            {preview.data.invalidCodes.map((code) => (
              <Typography key={code.code} variant="body2" color="error" data-testid="bil-package-invalid-code">
                {t('bil:packages.invalid_code', { code: code.code, reason: t(`cat:discount_reasons.${code.reason}`, { defaultValue: code.reason }) })}
              </Typography>
            ))}
            <Typography variant="subtitle2" sx={{ mt: 1 }}>
              {t('bil:packages.credits')}
            </Typography>
            <Stack component="ul" sx={{ m: 0, pl: 2 }}>
              {preview.data.credits.map((credit, index) => (
                <li key={index} data-testid="bil-package-credit-preview" data-value={credit.value.amount}>
                  {chosen?.items.find((item) => item.serviceId === credit.serviceId)?.serviceName ?? t('bil:packages.flexible')} ·{' '}
                  <Amount money={credit.value} />
                </li>
              ))}
            </Stack>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {t('bil:packages.expires_on')} <JalaliDate value={preview.data.expiresOn} />
            </Typography>
            <PolicySummary policy={preview.data.policy} />
          </Paper>
        ) : null}
        {access.can('bil.payment.receive') ? (
          <FormControlLabel
            control={<Checkbox checked={toCashier} onChange={(event) => setToCashier(event.target.checked)}/>}
            label={t('bil:packages.go_cashier')}
          />
        ) : null}
      </Stack>
    </FormDialog>
  );
}

function PackageDetailDialog({ sale, onClose }: { sale: BilPackageSaleView; onClose: () => void }) {
  const { t } = useTranslation('bil');
  return (
    <FormDialog open titleKey="bil:packages.detail" onSubmit={onClose} onClose={onClose} isDirty={false} isSubmitting={false} submitKey="common:actions.close" maxWidth="md">
      <Stack spacing={2} sx={{ pt: 1 }} data-testid="bil-package-detail-dialog">
        <KeyValueList
          columns={3}
          items={[
            { labelKey: 'bil:packages.columns.name', value: `${sale.packageName} (v${faDigits(num(sale.packageVersion))})` },
            { labelKey: 'bil:packages.net', value: <Amount money={sale.net} /> },
            { labelKey: 'bil:packages.paid', value: <Amount money={sale.paid} /> },
            { labelKey: 'bil:packages.sold_on', value: <JalaliDate value={sale.soldOn} /> },
            { labelKey: 'bil:packages.columns.expires', value: <JalaliDate value={sale.expiresOn} /> },
            { labelKey: 'bil:packages.invoice', value: sale.invoiceNumber ?? '—' },
          ]}
        />
        <PolicySummary policy={sale.policy} />
        <Table size="small" aria-label={t('bil:packages.credits')}>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>{t('bil:packages.service')}</TableCell>
              <TableCell>{t('bil:packages.value')}</TableCell>
              <TableCell>{t('bil:packages.columns.status')}</TableCell>
              <TableCell>{t('bil:packages.closed_at')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sale.credits.map((credit) => (
              <TableRow key={credit.id} data-testid="bil-package-credit">
                <TableCell>{faDigits(num(credit.sequence))}</TableCell>
                <TableCell>{credit.serviceName ?? t('bil:packages.flexible')}</TableCell>
                <TableCell>
                  <Amount money={credit.value} />
                </TableCell>
                <TableCell>
                  <CreditStatusChip status={credit.status} />
                </TableCell>
                <TableCell>{credit.closedAt ? <JalaliDate value={credit.closedAt} /> : '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Stack>
    </FormDialog>
  );
}

/** لغو با نمایش محاسبه استرداد (AC-BIL-073.1) و دلیل الزامی (AC-BIL-073.2). */
function CancelPackageDialog({ sale, onClose, onDone }: { sale: BilPackageSaleView; onClose: () => void; onDone: (creditNoteNumber: string | null) => void }) {
  const { tenantApi, session } = useTenantSession();
  const key = useIdempotencyKey();
  const calculation = useQuery({
    queryKey: [...bilKeys.packageSale(session?.tenantId, sale.id), 'cancellation'],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/billing/package-sales/{id}/cancellation', { params: { path: { id: sale.id } } })),
  });
  const cancel = useMutation({
    mutationFn: (reason: string) =>
      unwrap(
        tenantApi.POST('/api/v1/billing/package-sales/{id}:cancel', {
          params: { path: { id: sale.id } },
          body: { reason },
          headers: { 'Idempotency-Key': key.current() },
        }),
      ),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: (result) => onDone(result.creditNoteNumber),
  });
  const calc = calculation.data;
  return (
    <ReasonDialog open titleKey="bil:packages.cancel" messageKey="bil:packages.cancel_message" busy={cancel.isPending} error={cancel.error} onConfirm={(reason) => cancel.mutate(reason)} onCancel={onClose}>
      {calculation.error ? <ErrorState error={calculation.error} compact /> : null}
      {calc ? (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 1 }} data-testid="bil-package-cancellation">
          <KeyValueList
            columns={2}
            items={[
              { labelKey: 'bil:packages.paid', value: <Amount money={calc.paid} /> },
              { labelKey: 'bil:packages.consumed', value: faDigits(num(calc.consumedSessions)) },
              { labelKey: `bil:packages.basis.${calc.refundBasis}`, value: <Amount money={calc.usedCharge} testId="bil-cancel-used" /> },
              { labelKey: 'bil:packages.penalty', value: <Amount money={calc.penalty} testId="bil-cancel-penalty" /> },
              { labelKey: 'bil:packages.refundable', value: <Amount money={calc.refundable} testId="bil-cancel-refundable" /> },
              { labelKey: 'bil:packages.credit_note', value: <Amount money={calc.creditNote} testId="bil-cancel-credit-note" /> },
            ]}
          />
        </Paper>
      ) : null}
    </ReasonDialog>
  );
}
